import { ArrowLeft02Icon, Delete02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { Head, Link, router, useForm } from "@inertiajs/react";
import { useState } from "react";
import {
	type Conflict,
	ConflictDialog,
	findConflicts,
	mergeValues,
} from "@/components/conflict-dialog";
import type { Country } from "@/components/country-select";
import Dashboard from "@/components/dashboard";
import {
	type EditingPerson,
	EditingPersonItem,
} from "@/components/editing-person-item";
import {
	PERSON_FIELD_LABELS,
	type PersonFormData,
	PersonFormFields,
} from "@/components/person-form-fields";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogMedia,
	AlertDialogTitle,
	AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { VerificationStatusBadge } from "@/components/verification-status-badge";
import { getRelationshipLabel } from "@/lib/labels";
import type HomeUser from "@/types/serializers/HomeUser";
import type PhysicalPersonsPhysicalPersonDetail from "@/types/serializers/PhysicalPersonsPhysicalPersonDetail";

interface Props {
	user: HomeUser;
	physicalPerson: PhysicalPersonsPhysicalPersonDetail;
	countries: Country[];
}

interface FormData {
	physical_person: PersonFormData;
}

export default function PhysicalPersonShow({
	user,
	physicalPerson,
	countries,
}: Props) {
	const [editingPerson, setEditingPerson] = useState<EditingPerson>();
	const [pending, setPending] = useState<{
		conflicts: Conflict[];
		mergedValues: PersonFormData;
		description: string;
	}>();

	const { data, setData, patch, processing, errors, clearErrors, transform } =
		useForm<FormData>({
			physical_person: {
				first_name: physicalPerson.first_name,
				last_name: physicalPerson.last_name,
				birth_number: physicalPerson.birth_number ?? "",
				street: physicalPerson.street,
				city: physicalPerson.city,
				postal_code: physicalPerson.postal_code,
				country: physicalPerson.country,
				document_type: physicalPerson.document_type,
				document_number: physicalPerson.document_number,
				document_expiry_date: physicalPerson.document_expiry_date,
				document_issuer: physicalPerson.document_issuer,
				birth_date: physicalPerson.birth_date,
				birth_place: physicalPerson.birth_place,
				birth_country: physicalPerson.birth_country,
				citizenship: physicalPerson.citizenship,
				citizenship_other: physicalPerson.citizenship_other,
				gender: physicalPerson.gender,
				is_sanctioned: physicalPerson.is_sanctioned,
				is_pep: physicalPerson.is_pep,
				pep_info: physicalPerson.pep_info,
			},
		});

	const defaults: Partial<PersonFormData> = { country: "CZ" };

	const applyOrShowConflicts = (
		values: PersonFormData,
		description: string,
	) => {
		const merged = mergeValues(data.physical_person, values);
		const conflicts = findConflicts(
			data.physical_person,
			values,
			PERSON_FIELD_LABELS,
			defaults,
		);
		if (conflicts.length === 0) {
			setData("physical_person", merged);
		} else {
			setPending({
				conflicts,
				mergedValues: merged,
				description,
			});
		}
	};

	const handleApply = () => {
		if (pending) setData("physical_person", pending.mergedValues);
		setPending(undefined);
	};

	const handleCancel = () => {
		setPending(undefined);
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		transform((data) => ({
			physical_person: {
				...data.physical_person,
				id: editingPerson?.id,
			},
		}));
		patch(`/physical_persons/${physicalPerson.id}`);
	};

	const handleDelete = () => {
		router.delete(`/physical_persons/${physicalPerson.id}`);
	};

	const directClients = physicalPerson.client_links.filter(
		(link) =>
			link.relationship === "direct_individual" ||
			link.relationship === "direct_entrepreneur",
	);
	const businessLinks = physicalPerson.client_links.filter(
		(link) =>
			link.relationship !== "direct_individual" &&
			link.relationship !== "direct_entrepreneur",
	);

	const warnings = {
		birth_number: physicalPerson.missing_birth_number,
		address: physicalPerson.missing_address,
		document: physicalPerson.missing_document,
		document_expiring: physicalPerson.document_expiring,
		document_expired: physicalPerson.document_expired,
		birth_info: physicalPerson.missing_birth_info,
		citizenship: physicalPerson.missing_citizenship,
		gender: physicalPerson.missing_gender,
		aml_check: physicalPerson.missing_aml_check,
	};

	return (
		<>
			<Head title={physicalPerson.full_name} />
			<Dashboard user={user}>
				<form
					onSubmit={handleSubmit}
					className="flex flex-1 flex-col overflow-hidden"
				>
					<div className="overflow-y-auto p-4 lg:p-6">
						<div className="mx-auto flex w-full max-w-2xl flex-col gap-6">
							<div className="flex items-center justify-between">
								<div className="flex items-start gap-4">
									<Button
										type="button"
										variant="ghost"
										size="icon"
										render={<Link href="/physical_persons" />}
									>
										<HugeiconsIcon icon={ArrowLeft02Icon} strokeWidth={2} />
									</Button>
									<div>
										<div className="flex items-center gap-3">
											<h2 className="font-semibold text-2xl">
												{physicalPerson.full_name}
											</h2>
											<Badge variant="outline">Osoba</Badge>
										</div>
									</div>
								</div>
								<AlertDialog>
									<AlertDialogTrigger
										render={
											<Button type="button" variant="destructive">
												<HugeiconsIcon
													icon={Delete02Icon}
													strokeWidth={2}
													data-icon="inline-start"
												/>
												Smazat
											</Button>
										}
									/>
									<AlertDialogContent size="sm">
										<AlertDialogHeader>
											<AlertDialogMedia className="bg-destructive/10 text-destructive dark:bg-destructive/20 dark:text-destructive">
												<HugeiconsIcon icon={Delete02Icon} strokeWidth={2} />
											</AlertDialogMedia>
											<AlertDialogTitle>Smazat osobu?</AlertDialogTitle>
											<AlertDialogDescription>
												Tato akce je nevratná.
												{directClients.length > 0 &&
													" Smažou se také propojení klienti"}
												{directClients.length > 0 &&
													businessLinks.length > 0 &&
													" a osoba bude odebrána z propojených právnických osob."}
												{directClients.length > 0 &&
													businessLinks.length === 0 &&
													"."}
												{directClients.length === 0 &&
													businessLinks.length > 0 &&
													" Osoba bude odebrána z propojených právnických osob."}
											</AlertDialogDescription>
										</AlertDialogHeader>
										<AlertDialogFooter>
											<AlertDialogCancel variant="outline">
												Zrušit
											</AlertDialogCancel>
											<AlertDialogAction
												variant="destructive"
												onClick={handleDelete}
											>
												Smazat
											</AlertDialogAction>
										</AlertDialogFooter>
									</AlertDialogContent>
								</AlertDialog>
							</div>

							{editingPerson && (
								<EditingPersonItem
									person={editingPerson}
									onCancel={() => setEditingPerson(undefined)}
									variant="merge"
								/>
							)}

							<PersonFormFields
								value={data.physical_person}
								onChange={(person) => {
									setData("physical_person", person);
									clearErrors();
								}}
								errors={errors}
								countries={countries}
								onPersonSelected={(person) => {
									setEditingPerson(person);
									applyOrShowConflicts(
										person.formData,
										`Vybrali jste existující osobu ${person.formData.first_name} ${person.formData.last_name}.`,
									);
								}}
								warnings={warnings}
							/>

							<Card>
								<CardHeader>
									<CardTitle>Propojení s klienty</CardTitle>
								</CardHeader>
								<CardContent>
									{physicalPerson.client_links.length > 0 ? (
										<Table>
											<TableHeader>
												<TableRow>
													<TableHead>Klient</TableHead>
													<TableHead>Vztah</TableHead>
													<TableHead>Identifikace</TableHead>
												</TableRow>
											</TableHeader>
											<TableBody>
												{physicalPerson.client_links.map((link) => (
													<TableRow
														key={link.client_id}
														className="cursor-pointer"
														onClick={() =>
															router.visit(`/clients/${link.client_id}`)
														}
													>
														<TableCell className="font-medium">
															{link.client_name}
														</TableCell>
														<TableCell>
															{getRelationshipLabel(link.relationship)}
														</TableCell>
														<TableCell>
															<VerificationStatusBadge
																entries={[
																	{
																		verified: link.verified ?? false,
																		verification_expires_at:
																			link.verification_expires_at,
																	},
																]}
															/>
														</TableCell>
													</TableRow>
												))}
											</TableBody>
										</Table>
									) : (
										<p className="text-muted-foreground">
											Osoba není propojena s žádným klientem.
										</p>
									)}
								</CardContent>
							</Card>

							{pending && (
								<ConflictDialog
									conflicts={pending.conflicts}
									description={pending.description}
									onApply={handleApply}
									onCancel={handleCancel}
								/>
							)}
						</div>
					</div>

					<div className="border-t bg-background px-4 py-4 lg:px-6">
						<div className="mx-auto flex w-full max-w-2xl items-center justify-end">
							<div className="flex gap-4">
								<Button
									type="button"
									variant="outline"
									size="lg"
									render={<Link href="/physical_persons" />}
								>
									Zpět
								</Button>
								<Button type="submit" size="lg" disabled={processing}>
									{editingPerson ? "Sloučit osoby" : "Uložit změny"}
								</Button>
							</div>
						</div>
					</div>
				</form>
			</Dashboard>
		</>
	);
}
